"use client";

import { useTheme } from "next-themes";
import { useState } from "react";
import DemoFooter from "@/components/preview/demo-footer";
import DemoWrapper from "@/components/preview/demo-wrapper";
import SliderControl from "@/components/preview/slider-control";
import { ResultBadge } from "@/components/ui/result-badge";
import { useMounted } from "@/hooks/use-mounted";

const PAGE_LUMINANCE = { dark: 0.15 ** 3, light: 1 };

const SURFACE_LIGHTNESS = 0.5;
const SURFACE_LUMINANCE = SURFACE_LIGHTNESS ** 3;
const SURFACE_COLOR = `oklch(${SURFACE_LIGHTNESS} 0 0)`;

function grayLuminance(lightness: number) {
  const channel = lightness / 100;
  return channel <= 0.040_45
    ? channel / 12.92
    : ((channel + 0.055) / 1.055) ** 2.4;
}

function contrastRatio(lightness: number, background: number) {
  const text = grayLuminance(lightness);
  const lighter = Math.max(text, background);
  const darker = Math.min(text, background);
  return (lighter + 0.05) / (darker + 0.05);
}

export default function ColorsContrastDemo() {
  const [lightness, setLightness] = useState(90);
  const { resolvedTheme } = useTheme();
  const mounted = useMounted();

  const pageLuminance =
    mounted && resolvedTheme === "dark"
      ? PAGE_LUMINANCE.dark
      : PAGE_LUMINANCE.light;
  const pageRatio = contrastRatio(lightness, pageLuminance);
  const surfaceRatio = contrastRatio(lightness, SURFACE_LUMINANCE);
  const color = `hsl(0 0% ${lightness}%)`;

  return (
    <DemoWrapper margin="">
      <div className="flex h-56 w-full select-none flex-col items-center justify-center gap-5 px-6">
        <div
          className="rounded-xl px-6 py-5"
          style={{ background: SURFACE_COLOR }}
        >
          <p className="max-w-52 text-center text-sm" style={{ color }}>
            This text renders on the surface, not the page.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <span className="font-berkeley-mono text-gray-1000 text-sm">
              page {pageRatio.toFixed(1)}:1
            </span>
            <ResultBadge bad={pageRatio < 4.5} size="sm" />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-berkeley-mono text-gray-1000 text-sm">
              surface {surfaceRatio.toFixed(1)}:1
            </span>
            <ResultBadge bad={surfaceRatio < 4.5} size="sm" />
          </div>
        </div>
      </div>

      <DemoFooter>
        <SliderControl
          className="mb-1.5"
          label="Text lightness"
          max={100}
          min={0}
          onChange={setLightness}
          step={1}
          value={lightness}
          valueLabel={`${lightness}%`}
        />
      </DemoFooter>
    </DemoWrapper>
  );
}
